import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import './Salon.css';

const CreateSalon = () => {
  const { t, translateMessage } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/salons', formData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setSuccess(translateMessage(response.data.message));
      
      // Clear form
      setFormData({
        name: '',
        description: ''
      });

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);

    } catch (error) {
      if (error.response?.data?.code) {
        const code = error.response.data.code;
        // Handle subscription-specific errors
        if (code === 'TRIAL_EXPIRED' || code === 'SUBSCRIPTION_EXPIRED' || code === 'SUBSCRIPTION_INACTIVE') {
          setError('SUBSCRIPTION_EXPIRED');
        } else if (code === 'SALON_LIMIT_REACHED') {
          setError('SALON_LIMIT_REACHED');
        } else if (code === 'NO_SUBSCRIPTION') {
          setError('NO_SUBSCRIPTION');
        } else {
          setError(error.response.data.error);
        }
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else if (error.response?.data?.errors) {
        setError(error.response.data.errors[0].msg);
      } else {
        setError(t('salon.failedToCreateSalon'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  // Show subscription error page if needed
  if (error === 'SUBSCRIPTION_EXPIRED' || error === 'NO_SUBSCRIPTION') {
    return (
      <div className="salon-container">
        <div className="salon-card subscription-error">
          <div className="subscription-error-content">
            <div className="error-icon">⏰</div>
            <h2>{t('subscription.trialExpired')}</h2>
            <p>{t('subscription.trialExpiredMessage')}</p>
            <div className="error-actions">
              <button 
                className="salon-button primary large"
                onClick={() => navigate('/pricing')}
              >
                {t('subscription.viewPlans')}
              </button>
              <button 
                className="salon-button secondary"
                onClick={() => navigate('/dashboard')}
              >
                {t('navigation.backToDashboard')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error === 'SALON_LIMIT_REACHED') {
    return (
      <div className="salon-container">
        <div className="salon-card subscription-error">
          <div className="subscription-error-content">
            <div className="error-icon">🏢</div>
            <h2>{t('subscription.salonLimitReached')}</h2>
            <p>{t('subscription.salonLimitMessage')}</p>
            <div className="error-actions">
              <button 
                className="salon-button primary large"
                onClick={() => navigate('/pricing')}
              >
                {t('subscription.upgradePlan')}
              </button>
              <button 
                className="salon-button secondary"
                onClick={() => navigate('/dashboard')}
              >
                {t('navigation.backToDashboard')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="salon-container">
      <div className="salon-card">
        <div className="salon-header">
          <h1>{t('salon.createNewSalon')}</h1>
          <p>{t('salon.createNewSalonDesc')}</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
            <p>{t('salon.redirectingToDashboard')}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="create-new-salon-form">
          <div className="form-group">
            <label htmlFor="name">{t('salon.salonName')} *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder={t('salon.enterSalonName')}
              minLength="2"
              maxLength="255"
            />
            <small>{t('salon.chooseSalonName')}</small>
          </div>

          <div className="form-group">
            <label htmlFor="description">{t('common.description')}</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder={t('salon.describeSalon')}
              rows="4"
            />
            <small>{t('salon.describeSalonOptional')}</small>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="salon-button secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button 
              type="submit" 
              className="salon-button primary"
              disabled={loading || !formData.name.trim()}
            >
              {loading ? t('salon.creatingSalon') : t('salon.createSalon')}
            </button>
          </div>
        </form>

        <div className="salon-info">
          <h3>{t('salon.whatHappensNext')}</h3>
          <div className="info-steps">
            <div className="info-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>{t('salon.salonCreatedTitle')}</h4>
                <p>{t('salon.salonCreatedDesc')}</p>
              </div>
            </div>
            <div className="info-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>{t('salon.inviteTeamMembers')}</h4>
                <p>{t('salon.inviteTeamMembersDesc')}</p>
              </div>
            </div>
            <div className="info-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>{t('salon.startManaging')}</h4>
                <p>{t('salon.startManagingDesc')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateSalon;
